/**
 * `PlaylistSequence` — coluna direita do editor de playlist (TH-53378).
 *
 * Recebe a lista de slides em modo "rascunho" (estado local da página)
 * e renderiza um `SortableContext` vertical pra reorder via dnd-kit.
 * Cada linha é um `<PlaylistSlideRow>` (controlled — toda mutação volta
 * pra `PlaylistEditorTab`).
 *
 * Não chama API direto: pageparent monta o payload final no PUT.
 */

import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ListVideo } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  PlaylistSlideRow,
  type DraftSlide,
} from "@/components/playlist/PlaylistSlideRow";
import type { CustomArtRead } from "@/hooks/useCreateCustomArt";

export interface PlaylistSequenceProps {
  slides: DraftSlide[];
  customArtsById: ReadonlyMap<string, CustomArtRead>;
  onDurationChange: (localId: string, durationMs: number) => void;
  onActiveToggle: (localId: string) => void;
  onRemove: (localId: string) => void;
}

/**
 * Container droppable separado pra dar feedback visual quando um asset
 * da library tá sendo arrastado em cima da playlist vazia. O
 * `SortableContext` já trata reorder de itens existentes; este droppable
 * cobre o caso "adicionar novo asset".
 */
export function PlaylistSequence({
  slides,
  customArtsById,
  onDurationChange,
  onActiveToggle,
  onRemove,
}: PlaylistSequenceProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: "playlist-sequence-drop",
  });

  return (
    <div className="flex h-full flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-card)]">
      <header className="border-b border-[var(--color-border)] p-3">
        <h3 className="text-sm font-semibold">Sequência da playlist</h3>
        <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
          {slides.length === 0
            ? "Vazia. Arraste assets ou clique em Auto-gerar."
            : `${slides.length} ${slides.length === 1 ? "slide" : "slides"} — arraste pra reordenar.`}
        </p>
      </header>

      <div
        ref={setNodeRef}
        className={cn(
          "flex-1 overflow-y-auto p-3 transition-colors",
          isOver && "bg-[var(--color-primary)]/5",
        )}
      >
        {slides.length === 0 ? (
          <EmptyState highlighted={isOver} />
        ) : (
          <SortableContext
            items={slides.map((s) => s._localId)}
            strategy={verticalListSortingStrategy}
          >
            <ul role="list" className="flex flex-col gap-2">
              {slides.map((slide, idx) => (
                <PlaylistSlideRow
                  key={slide._localId}
                  slide={slide}
                  index={idx}
                  customArt={
                    slide.custom_art_id
                      ? customArtsById.get(slide.custom_art_id)
                      : undefined
                  }
                  onDurationChange={onDurationChange}
                  onActiveToggle={onActiveToggle}
                  onRemove={onRemove}
                />
              ))}
            </ul>
          </SortableContext>
        )}
      </div>
    </div>
  );
}

function EmptyState({ highlighted }: { highlighted: boolean }) {
  return (
    <div
      className={cn(
        "flex h-full min-h-[200px] flex-col items-center justify-center rounded-md border border-dashed",
        highlighted
          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
          : "border-[var(--color-border-strong)]",
      )}
    >
      <ListVideo
        className="h-8 w-8 text-[var(--color-muted-foreground)]"
        aria-hidden="true"
      />
      <p className="mt-2 text-sm text-[var(--color-foreground)]">
        Solte assets aqui.
      </p>
      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
        Ou clique em &ldquo;Auto-gerar a partir da área&rdquo;.
      </p>
    </div>
  );
}
